import { getBoundingBoxes } from 'api/v2/expenses';
import { get, omit, pick } from 'lodash';
import { useMemo, useState } from 'react';
import { useQuery } from 'react-query';
import type { BoundingBoxOCR } from 'types/ai.types';
import { ExpensesCacheKeys } from 'types/cacheKeys.types';
import { ExpenseEvidenceReasonEnum, IExpenseV2 } from 'types/expenses.types';
import { FIVE_MINUTES } from 'utils/time';

const normalizeFieldName = (fieldName: string) => {
  return fieldName.replace(/\b\d+\b/g, '');
};

const extractNumbers = (fieldName: string) => {
  return fieldName.match(/\b\d+\b/g) || [];
};

type BoxesMap = Record<string, { field: string; guessed: string }>;
const staticBoxesMap: BoxesMap = {
  'supplier.name': {
    field: 'supplier.name',
    guessed: 'guessed_data.supplier.name',
  },
  original_date: { field: 'expenseDate', guessed: 'guessed_data.date' },
  total_amount: {
    field: 'items.0.currencyAmount',
    guessed: 'guessed_data.total_amount',
  },
  is_invoice: { field: 'isInvoice', guessed: 'guessed_data.is_invoice' },
  currency: { field: 'currency', guessed: 'guessed_data.currency' },
};

type DynamicBoundingBoxesMap = Record<
  string,
  { field: string; guessed: string; matchPath?: string; matchs: string[] }[]
>;
const dynamicBoundingBoxesMap: DynamicBoundingBoxesMap = {
  'items..vat_regime.evidences..value': [
    {
      matchPath: 'items.{0}.vat_regime.evidences.{1}.reason',
      matchs: Object.values(
        pick(ExpenseEvidenceReasonEnum, [
          ExpenseEvidenceReasonEnum.local_vat_rate,
          ExpenseEvidenceReasonEnum.non_local_vat_rate,
          ExpenseEvidenceReasonEnum.zero_vat_found,
        ]),
      ),
      field: 'items.{0}.VATRate',
      guessed: 'guessed_data.items.{0}.vat_rate',
    },
    {
      matchPath: 'items.{0}.vat_regime.evidences.{1}.reason',
      matchs: Object.values(
        omit(ExpenseEvidenceReasonEnum, [
          ExpenseEvidenceReasonEnum.local_vat_rate,
          ExpenseEvidenceReasonEnum.non_local_vat_rate,
          ExpenseEvidenceReasonEnum.zero_vat_found,
        ]),
      ),
      field: 'items.{0}.VATRegime',
      guessed: 'guessed_data.items.{0}.vat_regime.vat_regime',
    },
  ],
};

const hydratePath = (path: string, variableNumbers: string[]) => {
  return path.replace(
    /{(\d+)}/g,
    (_, index: string) => variableNumbers[Number(index)],
  );
};

const generateBoxesMap = (
  boxes: BoundingBoxOCR[],
  guessedData: IExpenseV2['guessed_data'],
) => {
  const boxesMap: BoxesMap = {};

  boxes.forEach((box) => {
    const fieldName = normalizeFieldName(box.field_name);

    const result = dynamicBoundingBoxesMap[fieldName];

    if (!result) return null;

    const variableNumbers = extractNumbers(box.field_name);

    const match = result.find((item) => {
      const fieldNameValue = get(
        guessedData,
        item.matchPath
          ? hydratePath(item.matchPath, variableNumbers)
          : box.field_name,
      );
      return item.matchs.includes(fieldNameValue);
    });

    if (!match) return null;

    boxesMap[box.field_name] = {
      field: hydratePath(match.field, variableNumbers),
      guessed: hydratePath(match.guessed, variableNumbers),
    };
  });

  return boxesMap;
};

type UseBoundingBoxProps = {
  expenseId?: string;
  guessedData: IExpenseV2['guessed_data'];
  valueGetter: (name: string) => any;
};

type UseBoundingBoxReturn = {
  boundingBox: BoundingBoxOCR | undefined;
  setBoundingBoxField: (field: string | null) => void;
};

export const useBoundingBox = ({
  expenseId,
  guessedData,
  valueGetter,
}: UseBoundingBoxProps): UseBoundingBoxReturn => {
  const [boundingBoxField, setBoundingBoxField] = useState<string | null>(null);

  const enabled = !!expenseId && !!guessedData;

  const { data } = useQuery(
    [ExpensesCacheKeys.ExpenseBoundingBoxes, expenseId, enabled],
    () =>
      getBoundingBoxes(expenseId as string).then((data) => {
        return {
          entities: data.entities || [],
          boxesMap: {
            ...staticBoxesMap,
            ...generateBoxesMap(data.entities || [], guessedData),
          },
        };
      }),
    {
      enabled,
      cacheTime: FIVE_MINUTES,
    },
  );

  const boundingBox = useMemo(() => {
    const results = data?.entities?.filter((box) => {
      const fieldName = box.field_name;
      const { field, guessed } = data.boxesMap[fieldName] || {};

      if (field !== boundingBoxField || !box.vertices) return false;

      return valueGetter(guessed) === valueGetter(field);
    });

    const byMostContent = results?.sort(
      (a, b) => `${b.value || ''}`.length - `${a.value || ''}`.length,
    );

    return byMostContent?.[0];
  }, [data, boundingBoxField, valueGetter]);

  return { boundingBox, setBoundingBoxField };
};
